import countries from '../data/countries.json';

export const SAUDI_COUNTRY_VALUE = 'المملكة العربية السعودية';
export const SAUDI_PHONE_PREFIX = '+966';

/** Short JSON label → preferred Arabic form used across ERP forms. */
const COUNTRY_NAME_ALIASES = {
  السعودية: SAUDI_COUNTRY_VALUE,
};

/** Legacy slug codes stored on older product rows. */
export const LEGACY_COUNTRY_CODE_LABELS = {
  saudi: SAUDI_COUNTRY_VALUE,
  uae: 'الإمارات العربية المتحدة',
  china: 'الصين',
  algeria: 'الجزائر',
  france: 'فرنسا',
  germany: 'ألمانيا',
  italy: 'إيطاليا',
  turkey: 'تركيا',
  morocco: 'المغرب',
  tunisia: 'تونس',
};

function normalizeCountryName(name) {
  const trimmed = (name || '').trim();
  if (!trimmed) return '';
  return COUNTRY_NAME_ALIASES[trimmed] || trimmed;
}

/** All ISO countries (Arabic names), deduped and sorted. */
export function getWorldCountryNamesAr() {
  const names = countries
    .map((c) => normalizeCountryName(c.arabic_name))
    .filter(Boolean);
  return [...new Set(names)].sort((a, b) => a.localeCompare(b, 'ar'));
}

/** Example phone placeholder for Saudi forms. */
export function getBrandPhonePlaceholder() {
  return '+966 5X XXX XXXX';
}

/** Normalize a phone toward Saudi +966 when possible. */
export function normalizeBrandPhone(value) {
  const raw = (value || '').toString().trim();
  if (!raw) return '';

  const digits = raw.replace(/\D/g, '');
  if (raw.startsWith('+966') || raw.startsWith('966')) {
    return raw.startsWith('+') ? raw : `+${raw}`;
  }
  if (digits.startsWith('966')) return `+${digits}`;
  if (digits.startsWith('0') && digits.length >= 10) return `${SAUDI_PHONE_PREFIX}${digits.slice(1)}`;
  if (/^5\d{8}$/.test(digits)) return `${SAUDI_PHONE_PREFIX}${digits}`;
  return raw;
}

/** Returns true when a non-empty phone matches Saudi +966. */
export function isBrandPhoneValid(value) {
  const raw = (value || '').toString().trim();
  if (!raw) return true;
  const normalized = normalizeBrandPhone(raw);
  return normalized.startsWith(SAUDI_PHONE_PREFIX);
}

/** Default country for brand/manufacturer forms. */
export function getBrandDefaultCountry() {
  return SAUDI_COUNTRY_VALUE;
}

/**
 * Country select options — all world countries (Arabic), Saudi first.
 * @param {() => string} [_label] - unused; kept for call-site compatibility
 */
export function getBrandCountryOptions(_label) {
  const defaultCountry = SAUDI_COUNTRY_VALUE;
  const sorted = getWorldCountryNamesAr().filter((n) => n !== defaultCountry);
  return [
    { value: defaultCountry, label: defaultCountry, isDefault: true },
    ...sorted.map((name) => ({ value: name, label: name })),
  ];
}

/** Display label for a stored country value (slug code, Arabic name, or legacy i18n key). */
export function resolveCountryDisplayLabel(value, translateLegacy) {
  if (!value) return '';
  if (LEGACY_COUNTRY_CODE_LABELS[value]) return LEGACY_COUNTRY_CODE_LABELS[value];
  if (typeof translateLegacy === 'function') {
    const translated = translateLegacy(value);
    if (translated && translated !== value) return translated;
  }
  return normalizeCountryName(value) || value;
}

/** Options list including legacy slug codes still present in older records. */
export function getBrandCountryOptionsWithLegacy() {
  const base = getBrandCountryOptions();
  const seen = new Set(base.map((o) => o.value));
  for (const [code, label] of Object.entries(LEGACY_COUNTRY_CODE_LABELS)) {
    if (!seen.has(code) && !seen.has(label)) {
      base.push({ value: code, label });
      seen.add(code);
    }
  }
  return base;
}
